import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import PageTitleSubheader from '../../layout/subheader/PageTitleSubheader';
import { OutletContext } from '../../layout/Layout';
import {
	AlertVariant,
	Button,
	Grid,
	GridItem,
	Modal,
	ModalVariant,
	TextInput,
} from '@patternfly/react-core';
import ZiTable, { Action, Column } from '../../components/table/ZiTable';
import FilterTableLayout from '../../layout/FilterTableLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons/faPenToSquare';

import { useToast } from '@zeroedin-tech/zi-common-ui/lib/components/toast/ToastProvider';
import { Permission } from '../../enums/permission.enum';
import PermissionButton from '../../components/button/PermissionButton';
import { faTimes, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import {
	SystemSchedule as SystemScheduleClass,
	TEditSystemSchedule,
	TNewSystemSchedule,
	TNewSystemScheduleParam,
	TSystemSchedule,
} from '../../api/system-schedules/SystemSchedule';
import { useMount } from 'react-use';
import { TUser, User } from '../../api/security/User';
import { useUser } from '../../components/user/ApplicationProvider';

import ZiForm, {
	Field,
	UIType,
	ISelectOption,
	ZiFormHandle,
} from '@zeroedin-react/zi-common/lib/components/ZiForm/ZiForm';
import { AxiosError } from 'axios';
import DeleteConfirmationModal from '../../helpers/helper-components/DeleteConfirmationModal';

export default function SystemSchedule(): JSX.Element {
	const user = useUser();
	const { addToast } = useToast();
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
	const [data, setData] = useState<TSystemSchedule[]>([]);
	const [tableLoading, setTableLoading] = useState<boolean>(true);
	const [currentSchedule, setCurrentSchedule] = useState<TSystemSchedule>(
		SystemScheduleClass.Default()
	);
	const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
	const [users, setUsers] = useState<TUser[]>([]);
	const [parameters, setParameters] = useState<{ key: string; value: string }[]>([
		{ key: '', value: '' },
	]);
	const formRef = useRef<ZiFormHandle>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

	const handleExternalSubmit = () => {
		formRef.current?.submit();
	};

	useMount(() => {
		void User.GetAll().then((response) => {
			setUsers(response);
		});
	});

	const selectedColumns: Column<TSystemSchedule>[] = [
		{
			title: 'Title',
			columnName: 'title',
			sortable: true,
		},
		{
			title: 'Cron',
			columnName: 'cron',
			sortable: true,
		},
		{
			title: 'Disabled',
			columnName: 'disabled',
			sortable: true,
		},
		{
			title: 'Run Once',
			columnName: 'runOnce',
			sortable: true,
		},
		{
			title: 'Last Run',
			columnName: 'lastRun',
			sortable: true,
		},
		{
			title: 'Updated By',
			columnName: 'updater',
			sortable: true,
			customAccessor: (item) => {
				return users.find((x) => x.id == item.updater)?.name ?? '';
			},
		},
		{
			title: 'Updated At',
			columnName: 'updatedAt',
			sortable: true,
		},
	];

	const scheduleTypesOptions: ISelectOption[] = [
		{
			key: 'alert_checker',
			value: 'Check for Alerts',
			selectedByDefault: true,
		},
		{
			key: 'schema_update',
			value: 'Run Schema Update',
		},
		{
			key: 'data_import',
			value: 'Import Data',
		},
		{
			key: 'report_schedule',
			value: 'Run Report Schedules',
			isDisabled: true,
		},
	];
	const formProperties: Field<TNewSystemSchedule>[] = [
		{
			title: 'Title',
			columnName: 'title',
			uiSchema: {
				type: UIType.TEXT,
				placeholder: 'Title',
				helpText: 'The alias of the System Schedule to use in SQL statements.',
			},
			required: true,
		},
		{
			title: 'Command',
			columnName: 'command',
			uiSchema: {
				type: UIType.SELECT,
				options: scheduleTypesOptions,
				placeholder: 'Select Command',
				helpText: 'The Command to run for this System Schedule.',
				initialSelection: scheduleTypesOptions[0].key,
				onSelect: (selected: ISelectOption): string => {
					getRequiredParameters(selected.key.toString());

					// Update the currentSchedule state based on the selected command
					setCurrentSchedule((prev) => ({
						...prev,
						command_type: selected.key === 'alert_checker' ? 'cli' : 'queue',
						command: selected.key.toString(),
					}));

					return selected.key.toString();
				},
			},
			required: true,
		},
		{
			title: 'Cron',
			columnName: 'cron',
			uiSchema: {
				type: UIType.TEXT,
				placeholder: '* * * * *',
				helpText: 'The Cron string dictates how frequently your scheduled task will run.',
			},
			required: true,
		},

		{
			title: 'Run Local',
			columnName: 'runLocal',
			uiSchema: {
				type: UIType.BOOLEAN,
				helpText:
					'If enabled, the System Schedule will run on the local server. If disabled, it will run on the remote server.',
			},
		},
		{
			title: 'Run Once',
			columnName: 'runOnce',
			uiSchema: {
				type: UIType.BOOLEAN,
				helpText: 'If enabled, the System Schedule will only run once and then be deleted.',
			},
		},
		{
			title: 'Disabled',
			columnName: 'disabled',
			uiSchema: {
				type: UIType.BOOLEAN,
				helpText: 'If enabled, the System Schedule will not run until it is re-enabled.',
			},
		},
	];

	const actions: Action<SystemScheduleClass>[] = [
		{
			name: (
				<>
					<FontAwesomeIcon icon={faPenToSquare} />
					Edit
				</>
			),
			callback: (item) => {
				setCurrentSchedule(item);
				const scheduleParams: TNewSystemScheduleParam[] = [];

				if (item.parameters && item.parameters.length > 0) {
					item.parameters.map((param) => {
						if (param.value != '' && param.key != '') {
							scheduleParams.push({ key: param.key, value: param.value });
						}
					});
					setParameters(scheduleParams);
				} else {
					setParameters([{ key: '', value: '' }]);
				}

				setIsModalOpen(true);
			},
			permission: [Permission.CreateSystemSchedule, Permission.EditSystemSchedule],
		},
		{
			name: (
				<>
					<FontAwesomeIcon icon={faTrashAlt} />
					Delete
				</>
			),
			callback: (item) => {
				setCurrentSchedule(item);
				setIsDeleteModalOpen(true);
			},
			permission: Permission.DeleteSystemSchedule,
		},
	];

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle="System Schedules"
					pageDescription="Manage your System Schedules."
					expanded={subNavExpanded}
					setExpanded={setSubNavExpanded}
				/>
			),
		});

		getSystemSchedules();
	}, [setSubSide, subNavExpanded, setSubNavExpanded]);

	const handleAddParameterInputs = () => {
		setParameters([...parameters, { key: '', value: '' }]);
	};

	const handleParameterInputChange = (index: number, field: 'key' | 'value', value: string) => {
		const updatedInputs = [...parameters];
		updatedInputs[index][field] = value;
		setParameters(updatedInputs);
	};

	const getSystemSchedules = () => {
		SystemScheduleClass.GetAll(['parameters'])
			.then((SystemSchedules) => {
				setData(
					SystemSchedules.map((SystemSchedule) => {
						return {
							...SystemSchedule,
							parameters: SystemSchedule.parameters,
						};
					})
				);
				setTableLoading(false);
			})
			.catch(() => {
				addToast(
					'An error occurred while trying to load System Schedules. Please try again later.',
					AlertVariant.danger
				);
			});
	};

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const handleSuccess = (value: SystemScheduleClass) => {
		if (value.id) {
			addToast('System Schedule updated succesfully', AlertVariant.success);
		} else {
			addToast('System Schedule added succesfully', AlertVariant.success);
		}

		getSystemSchedules();
		setIsModalOpen(false);
	};
	const getRequiredParameters = (commandType: string) => {
		SystemScheduleClass.GetRequiredParameters(commandType)
			.then((response) => {
				if (response && response.length > 0) {
					const scheduleParams: TNewSystemScheduleParam[] = [];
					response.map((param) => {
						scheduleParams.push({ key: param, value: '' });
					});
					setParameters(scheduleParams);
					console.log(scheduleParams);
				} else {
					setParameters([{ key: '', value: '' }]);
				}
			})
			.catch(() => {
				addToast(
					'An error occurred while trying to load required parameters. Please try again later.',
					AlertVariant.danger
				);
			});
	};

	const deleteSystemSchedule = (scheduleId: number) => {
		setTableLoading(true);
		SystemScheduleClass.Delete(scheduleId)
			.then((response) => {
				if (response) {
					addToast('System Schedule deleted succesfully', AlertVariant.success);
					getSystemSchedules();
				}
				setTableLoading(false);
			})
			.catch(() => {
				setTableLoading(false);
				addToast(
					'An error occurred while trying to delete System Schedule. Please try again later.',
					AlertVariant.danger
				);
			})
			.finally(() => {
				setIsDeleteModalOpen(false);
			});
	};

	const closeDeleteModal = () => {
		setIsDeleteModalOpen(false);
	};

	const addButton = (
		<PermissionButton
			data-testid={'system-schedule-add-btn'}
			variant={'primary'}
			onClick={() => {
				setCurrentSchedule(SystemScheduleClass.Default());
				setIsModalOpen(true);
				setParameters([{ key: '', value: '' }]);
			}}
			permission={Permission.CreateSystemSchedule}
		>
			New System Schedule
		</PermissionButton>
	);

	const systemScheduleTable = (
		<ZiTable<SystemScheduleClass>
			ariaLabel={'System Schedules table'}
			columns={selectedColumns}
			data={data}
			caption="System Schedules"
			actions={actions}
			loading={tableLoading}
		/>
	);

	const removeParameterRow = (indexToExclude: number) => {
		setParameters(parameters.filter((_, index) => index !== indexToExclude));
	};

	const saveSystemSchedule = (currentSchedule: TSystemSchedule) => {
		setIsFormLoading(true);
		const scheduleParamsToSave: TNewSystemScheduleParam[] = [];
		parameters.map((param) => {
			if (param.value != '' && param.key != '') {
				scheduleParamsToSave.push({ key: param.key, value: param.value });
			}
		});

		if (currentSchedule && currentSchedule.id !== undefined && currentSchedule.id > 0) {
			const request: TEditSystemSchedule = {
				command_type: currentSchedule.command == 'alert_checker' ? 'cli' : 'queue',
				creator: currentSchedule.creator,
				cron: currentSchedule.cron,
				disabled: currentSchedule.disabled,
				enabled_since: currentSchedule.enabled_since,
				command: currentSchedule.command,
				parameters: scheduleParamsToSave,
				runLocal: currentSchedule.runLocal,
				runOnce: currentSchedule.runOnce,
				updater: user.id,
				id: currentSchedule.id,
				title: currentSchedule.title,
			};
			SystemScheduleClass.Patch(request)
				.then((request) => {
					handleSuccess(request);
				})
				.catch((error: AxiosError) => {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					const errorParsed = error.response?.data as any;
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
					const errorMsg = errorParsed?.messages[0] as string;
					if (errorMsg) {
						addToast(errorMsg, AlertVariant.danger);
					} else {
						addToast(
							'An error occurred while trying to save the System Schedule. Please try again later.',
							AlertVariant.danger
						);
					}
				})
				.finally(() => {
					setCurrentSchedule(currentSchedule);
					setIsFormLoading(false);
				});
		} else {
			const request: TNewSystemSchedule = {
				command_type: currentSchedule.command == 'alert_checker' ? 'cli' : 'queue',
				creator: user.id,
				cron: currentSchedule.cron,
				disabled: currentSchedule.disabled,
				enabled_since: currentSchedule.enabled_since,
				command: currentSchedule.command,
				parameters: scheduleParamsToSave,
				runLocal: currentSchedule.runLocal,
				runOnce: currentSchedule.runOnce,
				updater: user.id,
				title: currentSchedule.title,
			};
			SystemScheduleClass.Post(request)
				.then(() => {
					handleSuccess(request as SystemScheduleClass);
				})
				.catch((error: AxiosError) => {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					const errorParsed = error.response?.data as any;
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
					const errorMsg = errorParsed?.messages[0] as string;
					if (errorMsg) {
						addToast(errorMsg, AlertVariant.danger);
					} else {
						addToast(
							'An error occurred while trying to save the System Schedule. Please try again later.',
							AlertVariant.danger
						);
					}
				})
				.finally(() => {
					setCurrentSchedule(currentSchedule);
					setIsFormLoading(false);
				});
		}
	};

	return (
		<React.Fragment>
			<Modal
				variant={ModalVariant.large}
				title={currentSchedule.id ? 'Edit System Schedule' : 'New System Schedule'}
				isOpen={isModalOpen}
				onClose={handleClose}
			>
				<ZiForm<TSystemSchedule>
					ref={formRef}
					title={'New System Schedule'}
					fields={formProperties}
					initialSubject={currentSchedule ?? SystemScheduleClass.Default()}
					isLoading={isFormLoading}
					hideSubmitBtn={true}
					onSubmit={(formSchedule: TSystemSchedule) => {
						saveSystemSchedule(formSchedule);
					}}
				/>
				{!isFormLoading && (
					<>
						<br />
						<div className={'add-parameter-section'}>
							<div className={'add-parameter-header'}>
								<h2>Parameters:</h2>
								<Button
									variant="primary"
									onClick={handleAddParameterInputs}
									className="add-parameter-button pull-right"
									style={{ bottom: '25px' }}
								>
									Add Parameter
								</Button>
							</div>
							<Grid
								hasGutter
								span={12}
							>
								<GridItem span={6}>
									<label>Key:</label>
								</GridItem>
								<GridItem span={5}>
									<label>Value:</label>
								</GridItem>
								{parameters.map((input, index) => (
									<>
										<GridItem
											span={6}
											key={index}
										>
											<TextInput
												value={input.key}
												onChange={(value) =>
													handleParameterInputChange(index, 'key', value)
												}
												placeholder="Enter key"
											/>
										</GridItem>
										<GridItem span={5}>
											<TextInput
												value={input.value}
												onChange={(value) =>
													handleParameterInputChange(
														index,
														'value',
														value
													)
												}
												placeholder="Enter value"
											/>
										</GridItem>
										<GridItem span={1}>
											<Button
												variant="primary"
												onClick={() => {
													removeParameterRow(index);
												}}
											>
												<FontAwesomeIcon
													icon={faTimes}
													style={{ color: 'white' }}
												/>
											</Button>
										</GridItem>
									</>
								))}
							</Grid>
						</div>
						<br />
						<Button
							className="pull-right"
							variant="primary"
							onClick={handleExternalSubmit}
						>
							Save
						</Button>
						<br />
					</>
				)}
			</Modal>
			<FilterTableLayout
				table={systemScheduleTable}
				layoutActions={[addButton]}
			/>
			<DeleteConfirmationModal
				isOpen={isDeleteModalOpen}
				onClose={closeDeleteModal}
				onSubmit={() => {
					deleteSystemSchedule(currentSchedule.id);
				}}
			/>
		</React.Fragment>
	);
}
